import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2, Users, HardDrive, AlertTriangle, Info } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, LoadingState, ErrorState, StatCard, StatusBadge, EmptyState, Card } from "@/components/common";
import { useFetch } from "@/lib/useFetch";
import { fmtBytes } from "@/lib/format";
import { t } from "@/i18n";

export function GaragesTable({ garages }) {
  const navigate = useNavigate();
  if (!garages.length) return <EmptyState icon={Building2} title={t("admin.noGarages")} text={t("admin.noGaragesText")} />;
  return (
    <div className="gf-card overflow-x-auto">
      <Table data-testid="admin-garages-table">
        <TableHeader>
          <TableRow>
            <TableHead>{t("admin.col.garage")}</TableHead>
            <TableHead>{t("admin.col.subscription")}</TableHead>
            <TableHead>{t("admin.col.plan")}</TableHead>
            <TableHead>{t("admin.col.internalUsers")}</TableHead>
            <TableHead>{t("admin.col.portalAccounts")}</TableHead>
            <TableHead>{t("admin.col.storage")}</TableHead>
            <TableHead>{t("admin.col.publicLink")}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {garages.map((g) => (
            <TableRow key={g.id} onClick={() => navigate(`/admin/garages/${g.id}`)} className="cursor-pointer transition-colors duration-150 hover:bg-slate-50" data-testid={`admin-garage-row-${g.slug}`}>
              <TableCell>
                <Link to={`/admin/garages/${g.id}`} className="font-semibold text-[#0A2540] hover:text-teal-700" data-testid={`admin-garage-link-${g.slug}`}>{g.name}</Link>
                <p className="text-xs text-slate-500">/g/{g.slug}{g.is_demo ? ` · ${t("common.demo")}` : ""}{g.is_test ? ` · ${t("admin.testGarage")}` : ""}{g.approval_status && g.approval_status !== "approved" ? ` · ${t(`p7.approval.status.${g.approval_status}`)}` : ""}</p>
              </TableCell>
              <TableCell><StatusBadge status={g.subscription_status} /></TableCell>
              <TableCell>{g.plan_name || "—"}</TableCell>
              <TableCell>{g.internal_users}{g.max_internal_users ? ` / ${g.max_internal_users}` : ""}</TableCell>
              <TableCell>{g.portal_accounts}</TableCell>
              <TableCell>{fmtBytes(g.storage_bytes)}{g.photo_quota_mb != null ? ` / ${g.photo_quota_mb} MB` : ""}</TableCell>
              <TableCell><StatusBadge status={g.public_link_active ? "live" : "offline"} /></TableCell>
              <TableCell className="text-right">
                <Button asChild size="sm" variant="outline" onClick={(e) => e.stopPropagation()} data-testid={`admin-garage-manage-${g.slug}`}><Link to={`/admin/garages/${g.id}`}>{t("p7.admin.manage")}</Link></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function AdminDashboard() {
  const { data, error, loading, reload } = useFetch("/admin/stats");
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const c = data.subscription_counts;
  const totalStorage = data.garages.reduce((a, g) => a + g.storage_bytes, 0);
  return (
    <div>
      <PageHeader eyebrow={t("admin.area")} title={t("admin.dashboardTitle")} subtitle={t("admin.dashboardSubtitle")} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t("admin.stat.garages")} value={data.garages_count} icon={Building2} testId="stat-garages" />
        <StatCard label={t("admin.stat.users")} value={data.users_count} icon={Users} testId="stat-users" />
        <StatCard label={t("admin.stat.attention")} value={c.past_due + c.suspended} icon={AlertTriangle} testId="stat-attention" hint={t("admin.stat.attentionHint")} />
        <StatCard label={t("admin.stat.storage")} value={fmtBytes(totalStorage)} icon={HardDrive} testId="stat-storage" />
      </div>
      <div className="mt-6 flex flex-wrap gap-2" data-testid="subscription-status-counts">
        {Object.entries(c).map(([s, n]) => (
          <span key={s} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm">
            <StatusBadge status={s} /> <strong data-testid={`status-count-${s}`}>{n}</strong>
          </span>
        ))}
      </div>
      <Card className="mt-6 flex items-start gap-3 border-teal-200 bg-teal-50/60 p-4" testId="revenue-separation-note">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
        <p className="text-sm text-teal-900">{t("admin.revenueNote")}</p>
      </Card>
      <h2 className="mb-4 mt-10 text-xl font-bold text-slate-900">{t("admin.garagesOverview")}</h2>
      <GaragesTable garages={data.garages} />
    </div>
  );
}
